import React from "react";
import styled from "styled-components/macro";
import type { WithChildren } from "../../types/types";
import { InvisibleButton } from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { ChipContainer } from "../Chips/Chips";
import { XIcon } from "../Icons/Icons";

type DeletableChipDivProps = {
  active?: boolean;
  invertBG?: boolean;
};

const DeletableChipDiv = styled.div<DeletableChipDivProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.primitiveTokens.spacing.spacing_xs};
  position: relative;
  border-radius: ${({ theme }) => theme.primitiveTokens.radii.radius_xs};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.semanticColors.borderLighter};
  padding: ${({ theme }) =>
    `${theme.primitiveTokens.spacing.spacing_xs} ${theme.primitiveTokens.spacing.spacing_md}`};
  font-size: ${({ theme }) => theme.primitiveTokens.fontSizes.font_size_md};
  font-weight: ${({ theme }) =>
    theme.primitiveTokens.fontWeights.font_weight_regular};
  line-height: ${({ theme }) =>
    theme.primitiveTokens.lineHeights.line_height_md};
  color: ${(props) =>
    props.active
      ? props.theme.semanticColors.textSecondary
      : props.theme.semanticColors.textPrimary};
  &:hover,
  &:focus {
    color: ${({ theme }) => theme.semanticColors.textPrimary};
  }
  outline: none;
  button {
    transform: translate(0px, 5px);
    padding: 0;
    &:hover,
    &:focus {
      > svg {
        transform: scale(1.3);
      }
    }
  }
`;

const GreyText = styled.p`
  color: ${(props) => props.theme.secondaryTextColor};
  margin: 0;
`;

type DeletableChipProps = WithChildren<{
  active?: boolean;
  handleClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  invertBG?: boolean;
}>;

export const DeletableChip = ({
  children,
  handleClick,
  active,
  invertBG,
}: DeletableChipProps) => {
  return (
    <DeletableChipDiv
      active={active}
      invertBG={invertBG}
      style={{
        marginRight: "var(--spacing_xs)",
        marginBottom: "var(--spacing_xs)",
      }}
    >
      {children}
      {active && (
        <InvisibleButton type="button" onClick={handleClick}>
          <XIcon height={16} width={16} />
        </InvisibleButton>
      )}
    </DeletableChipDiv>
  );
};

type DeletableChipsProps = {
  chips: ChipType[];
  noneSelectedName: string;
  handleClick: (name: string) => void;
  invertBG?: boolean;
  showNoneSelectedMessage?: boolean;
};

export const DeletableChips = ({
  chips,
  noneSelectedName,
  handleClick,
  invertBG,
  showNoneSelectedMessage = true,
}: DeletableChipsProps) => {
  if (chips.length > 0) {
    return (
      <ChipContainer>
        {chips.map((chip) => (
          <DeletableChip
            handleClick={() => handleClick(chip.name)}
            active={chip?.active ?? true}
            invertBG={invertBG}
            key={chip.name}
          >
            {chip.name}
          </DeletableChip>
        ))}
      </ChipContainer>
    );
  } else if (showNoneSelectedMessage) {
    return <GreyText>{`No ${noneSelectedName} selected.`}</GreyText>;
  } else {
    return <></>;
  }
};
